import MomentUtils from '@date-io/moment';
import { FormControl } from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import { FormBodyAPI } from 'src/constants';
import { VariableContractField } from 'src/entities/Contract';
import { RecipientSelector } from 'src/features/recipient-selector/RecipientSelector';
import { ResizableTextField } from 'src/legacy/components/TextField';
import { FieldType } from 'src/services/api';
import { BlackHeadings, GraySmall } from 'src/theme/colors';
import { ensureUnreachable } from 'src/utils/common_utils';

import { DateTextField } from '../legacy/components/UI/Date/useAddDateForm';

interface useShareContractParams {
  variableInputs: VariableContractField[];
  selectedTemplateRef: string;
}

const useStyles = makeStyles()((theme) => ({
  copyText: {
    wordBreak: 'break-all',
    color: BlackHeadings,
  },
  iconButton: {
    padding: 0,
  },
  copyIcon: {
    fontSize: 12,
  },
  inputs: {
    marginTop: theme.spacing(2),
  },
  muted: {
    color: GraySmall,
  },
  allClientSelector: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
}));

export const useShareContract = ({
  variableInputs,
}: useShareContractParams) => {
  const { t } = useTranslation();
  // Creating validation schema for all the variable inputs
  // We want all the variable inputs to be required
  const generateValidationSchema = useMemo(() => {
    const yupSchema: Record<
      string,
      Yup.StringSchema<string> | Yup.DateSchema<Date>
    > = {};
    if (variableInputs?.length > 0) {
      variableInputs.forEach((input) => {
        if (input.id)
          yupSchema[input.id] =
            input.type === FieldType.Date
              ? Yup.date().required('Field is required')
              : Yup.string()
                  .required('Field is required')
                  .max(1000, "Field can't be more than 1000 characters");
      });
    }
    return yupSchema;
  }, [variableInputs]);

  const validationScheme = Yup.object().shape({
    recipientIds: Yup.array()
      .of(
        Yup.object().shape({
          clientId: Yup.string(),
          companyId: Yup.string(),
        }),
      )
      .required(t(($) => $.validation.channels.select_at_least_one_client)),
    fields: Yup.object().shape(generateValidationSchema),
  });

  const FormRenderer = useCallback(
    ({
      values,
      errors,
      handleBlur,
      handleChange,
      setFieldValue,
      touched,
    }: FormBodyAPI) => {
      const { classes } = useStyles();
      const hasVariableInputs = variableInputs?.length > 0;

      const renderVariableInputs = () =>
        variableInputs.map((input) => {
          switch (input.type) {
            case FieldType.Date:
              return (
                <FormControl fullWidth className={classes.inputs}>
                  <MuiPickersUtilsProvider utils={MomentUtils}>
                    <KeyboardDatePicker
                      label={input.label}
                      TextFieldComponent={DateTextField}
                      disableToolbar
                      name={`fields.${input.id}`}
                      margin="none"
                      variant="inline"
                      inputVariant="outlined"
                      format="MM/DD/YYYY"
                      value={values.fields[input.id]}
                      onChange={(val) => {
                        setFieldValue(
                          `fields.${input.id}`,
                          moment(val).format('MM/DD/YYYY'),
                        );
                      }}
                      autoOk
                    />
                  </MuiPickersUtilsProvider>
                </FormControl>
              );
            case FieldType.Text:
              return (
                <FormControl fullWidth className={classes.inputs}>
                  <ResizableTextField
                    variant="outlined"
                    type="text"
                    label={input.label}
                    name={`fields.${input.id}`}
                    value={values.fields[input.id]}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={
                      touched?.fields &&
                      touched.fields[input.id] &&
                      errors?.fields &&
                      errors.fields[input.id]
                    }
                    helperText={
                      touched?.fields &&
                      touched.fields[input.id] &&
                      errors?.fields &&
                      errors.fields[input.id]
                    }
                  />
                </FormControl>
              );
            case FieldType.Signature:
            case FieldType.Initials:
              return null;
            default:
              return ensureUnreachable(input.type);
          }
        });

      return (
        <div>
          <FormControl fullWidth className={classes.inputs}>
            <RecipientSelector
              ignoreFilterClientsByCompany
              recipientTypes={['client']}
              onRecipientSelected={(vals) => {
                const clientCompanyIds = vals.map((val) => ({
                  clientId: val.id,
                  companyId: val.companyId,
                }));
                setFieldValue('recipientIds', clientCompanyIds);
              }}
              name="contractRecipientIds"
              placeholder={t(($) => $.actions.select_clients)}
              error={Boolean(errors.recipientIds)}
              helperText={errors.recipientIds}
            />
          </FormControl>
          {hasVariableInputs && <div>{renderVariableInputs()}</div>}
        </div>
      );
    },
    [variableInputs],
  );
  return { validationScheme, FormRenderer };
};
